import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function NotificationModal({ isOpen, onClose, notification }) {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);


  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Play notification sound
      playNotificationSound();
      // Highlight/focus the tab
      highlightTab();
    }
  }, [isOpen]);

  const playNotificationSound = () => {
    try {
      // Create a pleasant notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillators for a pleasant chord
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Connect nodes
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Set frequencies for a pleasant notification sound (C and E notes)
      oscillator1.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator2.frequency.setValueAtTime(659.25, audioContext.currentTime); // E5
      
      // Set gain (volume)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      // Play the sound
      oscillator1.start(audioContext.currentTime);
      oscillator2.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.5);
      oscillator2.stop(audioContext.currentTime + 0.5);
      
    } catch (error) {
      console.warn('Could not play notification sound:', error);
      
      // Fallback: try to use a simple beep
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmMbBThxz+vMfC8FJHjA5+OSUAoUY7Pl6qpZFAY+gdHwwmYcBjiRvvLUeiwEJWfH8+OKPgcYYK/q9K5aFAY8g8zvwGgdBjmIXTZj8ePyU2A=');
        audio.volume = 0.1;
        audio.play().catch(() => {}); // Ignore errors
      } catch (fallbackError) {
        // Silent fallback
        console.warn('Fallback sound also failed:', fallbackError);
      }
    }
  };

  const highlightTab = () => {
    try {
      // Focus the window
      window.focus();
      
      // Flash the title for attention
      const originalTitle = document.title;
      let flashCount = 0;
      const flashInterval = setInterval(() => {
        document.title = flashCount % 2 === 0 ? '🎉 Analysis Complete!' : originalTitle;
        flashCount++;
        if (flashCount >= 6) { // Flash 3 times
          clearInterval(flashInterval);
          document.title = originalTitle;
        }
      }, 500);
      
    } catch (error) {
      console.warn('Could not highlight tab:', error);
    }
  };

  const handleViewReport = () => {
    if (notification?.jobId) {
      navigate(`/dashboard/actions?jobId=${notification.jobId}`);
    }
    onClose();
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 150); // Allow animation to complete
  };

  if (!isOpen) return null;

  const domain = notification?.url
    ? (() => {
        try {
          const u = new URL(notification.url);
          const host = u.hostname.replace(/^www\./, '');
          const seg = u.pathname.split('/').filter(Boolean)[0];
          return seg ? `${host}/${seg}` : host;
        } catch {
          return notification.url;
        }
      })()
    : 'your website';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative bg-card rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ${
        isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Header with success icon */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-t-2xl p-6 text-center">
          <div className="w-16 h-16 bg-card rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white">Analysis Complete!</h2>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-lg text-card-foreground mb-2">
              Analysis of <strong>{domain}</strong> is complete.
            </p>
            <p className="text-2xl font-bold text-primary-600 mb-4">
              {notification?.actionCount || 0} actions generated
            </p>
            <p className="text-muted-foreground">
              Your personalized SEO action plan is ready to review.
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleViewReport}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              View Your Report
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-3 text-muted-foreground hover:text-gray-800 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 rounded-lg"
            >
              Later
            </button>
          </div>
        </div>
        
        {/* Celebration animation */}
        <div className="absolute -top-2 -right-2">
          <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
            <span className="text-lg">🎉</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationModal;